import React, { useState } from 'react';
import { Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import AudioPlayer from '../AudioPlayer/AudioPlayer';

export default function VersionPlayer({ 
  song, 
  version, 
  versionNumber, 
  isLatest = false,
  user,
  commenterName,
  onUpdateNotes,
  onAddComment,
  onDeleteComment
}) {
  const [editingNotes, setEditingNotes] = useState(false);
  const [tempNotes, setTempNotes] = useState(version.notes || '');
  const [showDiscussion, setShowDiscussion] = useState(false);
  const [newComment, setNewComment] = useState('');

  const commentCount = version.comments ? version.comments.length : 0;
  const hasNotes = version.notes && version.notes.trim().length > 0;

  const handleSaveNotes = async () => {
    const success = await onUpdateNotes(song.id, version.id, tempNotes);
    if (success) {
      setEditingNotes(false);
    }
  };

  const handleAddComment = async () => {
    const success = await onAddComment(song.id, version.id, newComment);
    if (success) {
      setNewComment('');
    }
  };

  return (
    <>
      <AudioPlayer audioUrl={version.fileURL} versionId={version.id} key={version.id} />
      
      {/* Compact notes - inline display with edit on click */}
      {(hasNotes || isLatest) && (
        <div className="mt-3">
          {editingNotes ? (
            <div className="space-y-2">
              <textarea 
                value={tempNotes}
                onChange={(e) => setTempNotes(e.target.value)}
                placeholder="Add notes about this version..." 
                className="w-full bg-transparent border border-surface-border-light rounded-card px-3 py-2 text-sm text-white placeholder-text-subtle focus:outline-none focus:border-brand resize-none" 
                rows="2"
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  onClick={handleSaveNotes}
                  className="bg-brand hover:bg-brand-hover transition px-3 py-1 text-xs font-semibold rounded-card-btn"
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setEditingNotes(false);
                    setTempNotes(version.notes || '');
                  }}
                  className="bg-transparent border border-surface-border-light hover:border-surface-border transition px-3 py-1 text-xs font-semibold rounded-card-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => {
                setEditingNotes(true);
                setTempNotes(version.notes || '');
              }}
              className="w-full text-left bg-white/5 hover:bg-white/10 transition rounded-lg px-3 py-2 text-sm"
            >
              {hasNotes ? (
                <span className="text-text-muted">{version.notes}</span>
              ) : (
                <span className="text-text-subtle italic">+ Add notes...</span>
              )}
            </button>
          )}
        </div>
      )}

      {/* Collapsible discussion toggle */}
      <button
        onClick={() => setShowDiscussion(!showDiscussion)}
        className="mt-3 flex items-center gap-2 text-sm text-text-subtle hover:text-white transition"
      >
        {showDiscussion ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <span>Discussion {commentCount > 0 && `(${commentCount})`}</span>
      </button>

      {/* Discussion content - only shown when expanded */}
      {showDiscussion && (
        <div className="mt-2 pl-6 border-l-2 border-surface-border-light">
          {version.comments && version.comments.length > 0 && (
            <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
              {version.comments.map(comment => (
                <div key={comment.id} className="border border-surface-border-light rounded-card-btn p-2 text-sm">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold text-brand text-xs">{comment.author}</span>
                      <span className="text-text-subtle text-xs ml-2">{new Date(comment.timestamp).toLocaleDateString()}</span>
                      <p className="text-white text-sm mt-0.5">{comment.text}</p>
                    </div>
                    {comment.userId === user.uid && (
                      <button
                        onClick={() => { if (window.confirm('Delete?')) onDeleteComment(song.id, version.id, comment.id); }}
                        className="text-text-subtle hover:text-red-400 transition ml-2"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyPress={(e) => { if (e.key === 'Enter') handleAddComment(); }}
              placeholder={`Comment as ${commenterName}...`}
              className="flex-1 bg-transparent border border-surface-border-light px-3 py-1.5 text-sm text-white placeholder-text-subtle focus:outline-none focus:border-brand rounded-pill"
            />
            <button
              onClick={handleAddComment}
              className="bg-brand hover:bg-brand-hover transition px-3 py-1.5 text-xs font-semibold rounded-pill"
            >
              Send
            </button>
          </div>
        </div>
      )}
    </>
  );
}
